// use-collection-drop — единый обработчик «бросили карточку на чип коллекции».
//
// D-решения:
//  • material | essay → прямое добавление через api.folders.addItems (оптимистично
//    через utils.folders.listForItem.setData + rollback; РЕАЛЬНЫЙ tRPC, без fake-
//    success — code-quality §28).
//  • остальные 5 типов (note/test/flashcard_set/audio_recap/visual_analysis) →
//    getItemBindingInfo-роутинг (§6.4): standalone → прямой addItems как материал;
//    bound → открываем модал «материал vs копия» (dragSession.openArtifactResolve
//    с materialId+materialTitle); смонтированный в layout AddArtifactToCollectionModal
//    дёргает реальные sets.addItems / sets.addArtifactCopy.
//  • setId === NEW_COLLECTION_DROP_ID (бросок на чип «Новая коллекция») → создаём
//    коллекцию по умолчанию (имя = заголовок элемента) через api.folders.create, затем
//    маршрутизируем тем же обработчиком (для bound — сперва модал с новым setId).
//    Inline-create по клику живёт в чипе.
//
// Haptics — только на mobile (gate IS_MOBILE_PLATFORM); desktop не вибрирует.

import { useHaptic } from "@studyhub/platform/hooks";
import { toast } from "@studyhub/ui/toast";
import { useTranslation } from "react-i18next";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";
import { type DragItem, useDragSessionStore } from "@/stores/drag-session";

/** Типы, добавляемые в коллекцию напрямую (без резолва «материал vs копия»). */
const DIRECT_ADD_TYPES: ReadonlySet<DragItem["type"]> = new Set(["material", "essay"]);

/** Sentinel droppable-id чипа «Новая коллекция» (синхронно с CollectionChips). */
export const NEW_COLLECTION_DROP_ID = "__new__";

/** Sentinel droppable-id цели «Выбрать коллекцию…» (открывает searchable-пикер). */
export const PICK_COLLECTION_DROP_ID = "__pick__";

export interface CollectionDropController {
	handleDrop: (item: DragItem, setId: string) => void;
}

export function useCollectionDrop(): CollectionDropController {
	const { t } = useTranslation("library");
	const utils = api.useUtils();
	const haptic = useHaptic();
	const openArtifactResolve = useDragSessionStore((s) => s.openArtifactResolve);
	const openPick = useDragSessionStore((s) => s.openPick);
	const pushLastUsed = useDragSessionStore((s) => s.pushLastUsed);

	const createSet = api.folders.create.useMutation();

	const addItems = api.folders.addItems.useMutation({
		onMutate: async ({ setId, items }) => {
			const item = items[0];
			if (!item) return { previous: undefined };
			const listKey = { type: item.type, id: item.id } as const;
			await utils.folders.listForItem.cancel(listKey);
			const previous = utils.folders.listForItem.getData(listKey);
			// Оптимистично помечаем коллекцию как содержащую элемент (если кэш уже
			// прогрет меню «в коллекцию» — отражаем checked сразу).
			utils.folders.listForItem.setData(listKey, (old) =>
				old
					? {
							sets: old.sets.map((s) => (s.id === setId ? { ...s, contains: true } : s)),
						}
					: old,
			);
			return { previous, listKey };
		},
		onError: (_err, _vars, ctx) => {
			if (ctx?.previous && ctx.listKey) {
				utils.folders.listForItem.setData(ctx.listKey, ctx.previous);
			}
			toast.error(t("dock.addError"));
			if (IS_MOBILE_PLATFORM) haptic.notification({ type: "error" });
		},
		onSettled: (_data, _err, vars) => {
			const item = vars.items[0];
			if (item) void utils.folders.listForItem.invalidate({ type: item.type, id: item.id });
			// Счётчик материалов на карточке папки и в рельсе Библиотеки. Раньше
			// здесь инвалидировался `folders.list` — эндпоинт БЕЗ единого читателя,
			// то есть вызов был холостым, а счётчик оставался старым до истечения
			// `staleTime`. Живой источник — `library.folders.list`.
			void utils.library.folders.list.invalidate();
		},
	});

	/** Прямое оптимистичное добавление элемента (material/essay/standalone). */
	function directAdd(item: DragItem, setId: string): void {
		addItems.mutate(
			{ setId, items: [{ type: item.type, id: item.id }] },
			{
				onSuccess: () => {
					toast.success(t("dock.added"));
					if (IS_MOBILE_PLATFORM) haptic.notification({ type: "success" });
					pushLastUsed(setId);
				},
			},
		);
	}

	/**
	 * getItemBindingInfo-роутинг для bound-capable артефактов (§6.4):
	 *  • standalone → directAdd (как материал);
	 *  • bound → открыть модал «материал vs копия» с binding-info.
	 * Ошибка fetch — громкая (toast), сессия не зависает.
	 */
	async function routeBoundCapable(item: DragItem, setId: string): Promise<void> {
		try {
			const info = await utils.folders.getItemBindingInfo.fetch({ type: item.type, id: item.id });
			if (info.state === "standalone") {
				directAdd(item, setId);
				return;
			}
			openArtifactResolve(item, setId, {
				materialId: info.materialId,
				materialTitle: info.materialTitle,
			});
			pushLastUsed(setId);
		} catch {
			toast.error(t("addArtifact.error"));
			if (IS_MOBILE_PLATFORM) haptic.notification({ type: "error" });
		}
	}

	function addToSet(item: DragItem, setId: string): void {
		if (DIRECT_ADD_TYPES.has(item.type)) {
			directAdd(item, setId);
			return;
		}
		void routeBoundCapable(item, setId);
	}

	function handleDrop(item: DragItem, setId: string): void {
		// Бросок на «Выбрать коллекцию…» — не добавляем сразу, а открываем пикер со
		// всеми коллекциями (масштабирование: десятки коллекций не уместить в док).
		if (setId === PICK_COLLECTION_DROP_ID) {
			openPick(item);
			return;
		}
		if (setId !== NEW_COLLECTION_DROP_ID) {
			addToSet(item, setId);
			return;
		}
		// Бросок на «Новая коллекция»: создаём набор (имя = заголовок элемента),
		// затем маршрутизируем в него. Реальный tRPC, не fake-success. Для bound-
		// артефакта addToSet сам откроет модал 6.5 с новым setId (§6.6).
		void createSet
			.mutateAsync({ name: item.title.slice(0, 200) })
			.then((created) => {
				void utils.library.folders.list.invalidate();
				addToSet(item, created.id);
			})
			.catch(() => {
				toast.error(t("dock.createError"));
				if (IS_MOBILE_PLATFORM) haptic.notification({ type: "error" });
			});
	}

	return { handleDrop };
}
